import { Badge, Box, Card, Group, Stack } from '@mantine/core';
import { IconBolt } from '@tabler/icons-react';
import { GetEvChargingStationResponse } from 'api/actions/get-ev-charging-station/get-ev-charging-station-response';
import { GetEvChargingStationLiveStatusResponse } from 'api/actions/get-ev-charging-station-live-status/get-ev-charging-station-live-status-response';
import useLang from 'lang';
import sprintf from 'lang/sprintf';
import { useEffect, useState } from 'react';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import P2Medium from 'theme/components/Typography/P2Medium';
import P4Medium from 'theme/components/Typography/P4Medium';
import P4Regular from 'theme/components/Typography/P4Regular';

interface ChargingStationCardProps {
  station: GetEvChargingStationResponse;
  /** The current customer's session in progress, from the live AgeVolt status. */
  activeSession: NonNullable<GetEvChargingStationLiveStatusResponse['activeSession']>;
  /** Maximum connector power of the station in kW, from the live AgeVolt status. */
  maxPowerKw: number | null;
}

/** Formats elapsed milliseconds as HH:MM:SS. */
function formatDuration(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  return [Math.floor(totalSeconds / 3600), Math.floor(totalSeconds / 60) % 60, totalSeconds % 60]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

/** Current time, updated every second while the component is mounted. */
function useNow() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  return now;
}

interface DetailRowProps {
  label: string;
  value: string;
}

/** A label/value row separated from the next row by a thin divider. */
function DetailRow({ label, value }: DetailRowProps) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <P4Regular color="gray.6">{label}</P4Regular>
      <P4Medium ta="right">{value}</P4Medium>
    </Group>
  );
}

/**
 * Station name/status and a live-session summary shown while the current
 * customer's car is charging at this station.
 *
 * Everything shown comes from the live AgeVolt status: the connector, the
 * current power (also as a share of the station's maximum power), delivered
 * energy, start time, a running duration, and the price so far (the delivered
 * energy at the customer's current tariff including VAT).
 *
 * The original design also had an estimated battery charge, an estimated
 * completion time, an estimated total price, and a stop button. They are
 * commented out below, each with the reason, until the data or the provider
 * function exists.
 */
export default function ChargingStationCard({ station, activeSession, maxPowerKw }: ChargingStationCardProps) {
  const { _t, lang } = useLang();
  const now = useNow();
  const pricePerKwhWithVat = station.pricePerKwhBeforeVat * (1 + station.vatPercentage / 100);
  /** Formats a euro amount for display. */
  const formatEur = (value: number) =>
    `${value.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  /** Formats a number with the given number of decimals, or a dash when unknown. */
  const formatNumber = (value: number | null, digits: number) =>
    value === null ? '–' : value.toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const { connectorId, startedAt, powerKw, energyKwh } = activeSession;
  const duration = startedAt ? formatDuration(now - Date.parse(startedAt)) : '–';
  const startTime = startedAt
    ? new Date(startedAt).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
    : '–';
  const powerShare = powerKw !== null && maxPowerKw ? Math.min(100, Math.max(0, (powerKw / maxPowerKw) * 100)) : 0;
  const location = station.buildingDisplay || _t('Location not set');
  const priceSoFar = energyKwh === null ? '–' : formatEur(energyKwh * pricePerKwhWithVat);

  return (
    <Stack gap={8} px={16} mt={-8} style={{ position: 'relative' }}>
      <Card p={16} radius={10}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={0}>
            <H2SemiBold>
              {station.name}
              {station.connectorCount !== null ? ` (${station.connectorCount})` : ''}
            </H2SemiBold>
            <P4Regular color="gray.7">
              {connectorId !== null ? `${location} / ${sprintf(_t('connector %d'), connectorId)}` : location}
            </P4Regular>
          </Stack>
          <Badge
            radius={20}
            variant="outline"
            color="gray.2"
            styles={{ label: { textTransform: 'none' } }}
            px={12}
            py={14}
            style={{ flexShrink: 0 }}
            leftSection={
              <IconBolt size={14} color="var(--mantine-color-green-6)" fill="var(--mantine-color-green-6)" />
            }
          >
            <P4Medium color="dark">{_t('Charging')}</P4Medium>
          </Badge>
        </Group>
      </Card>

      <Card p={16} radius={10}>
        <Stack gap={12}>
          <Group gap={4} align="baseline">
            <H1SemiBold>{formatNumber(powerKw, 1)}</H1SemiBold>
            <P4Medium color="gray.7">{_t('kW current power')}</P4Medium>
          </Group>

          {/*
            Estimated battery charge ("~ 58 %" with a progress bar) is hidden.
            Why: the AgeVolt wallboxes are AC chargers and AC charging does not
            report the car's state of charge (evse_last_data__soc is always
            null), and without the car's battery capacity it cannot be
            estimated from the delivered energy either. The bar below shows the
            current power against the station's maximum power instead.
          */}
          <Stack gap={4}>
            <Group justify="space-between" align="flex-end">
              <P4Regular color="gray.6">{_t('charging power')}</P4Regular>
              <P2Medium>
                {formatNumber(powerKw, 1)} / {formatNumber(maxPowerKw, 0)} kW
              </P2Medium>
            </Group>
            <Box h={24} bg="gray.2" style={{ borderRadius: 10, overflow: 'hidden' }}>
              <Box
                h="100%"
                w={`${powerShare}%`}
                style={{
                  borderRadius: 10,
                  backgroundImage: 'linear-gradient(to right, var(--mantine-color-green-6), #C6F135)',
                }}
              />
            </Box>
          </Stack>

          <Group grow py={12}>
            <Stack gap={0} py={8} px={12} style={{ border: `1px solid ${theme.colors?.gray?.[2]}`, borderRadius: 10 }}>
              <P2Medium>
                {pricePerKwhWithVat.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </P2Medium>
              <P4Regular color="gray.6">{_t('Price per kWh')}</P4Regular>
            </Stack>
            <Stack gap={0} py={8} px={12} style={{ border: `1px solid ${theme.colors?.gray?.[2]}`, borderRadius: 10 }}>
              <P2Medium>{priceSoFar}</P2Medium>
              <P4Regular color="gray.6">{_t('price for this charge')}</P4Regular>
            </Stack>
          </Group>

          <Stack gap={12} px={12}>
            <DetailRow label={_t('energy delivered')} value={`${formatNumber(energyKwh, 2)} kWh`} />
            <Box style={{ borderTop: `1px solid ${theme.colors?.gray?.[2]}` }} />
            <DetailRow label={_t('charging duration')} value={duration} />
            <Box style={{ borderTop: `1px solid ${theme.colors?.gray?.[2]}` }} />
            <DetailRow label={_t('charging started')} value={startTime} />
            {/*
              Estimated completion time ("19:21") is replaced by the start time above.
              Why: it would need the battery state of charge and capacity, which
              AgeVolt does not provide (see the charging power bar above).

              <DetailRow label={_t('estimated completion time')} value="19:21" />
            */}
            {/*
              Estimated total charging price ("24,30 €") is hidden.
              Why: the total depends on the completion estimate above, which is
              not available. The real price so far is shown in the tile above.

              <DetailRow label={_t('estimated total charging price')} value={formatEur(24.3)} />
            */}
          </Stack>

          {/*
            "Stop charging" button is hidden.
            Why: the AgeVolt API available to Phillip is read-only (it only
            exposes view export/select). Stopping a session remotely needs a
            control endpoint from AgeVolt (e.g. an OCPP RemoteStopTransaction
            proxy), which they do not offer yet. Drivers stop charging on the
            charger or by unplugging the car. Restore once AgeVolt provides it:

            <Group justify="center" gap={8} h={40} bg="gray.12" style={{ borderRadius: 10 }}>
              <IconPlayerStop size={16} color="white" fill="white" />
              <P4Medium color="white">{_t('Stop charging')}</P4Medium>
            </Group>
          */}
        </Stack>
      </Card>
    </Stack>
  );
}
